import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Save, Loader2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function EditarTraspaso({ clave, onClose, onSaved }) {
  const [doc, setDoc] = useState(null);
  const [items, setItems] = useState([]);
  const [motivo, setMotivo] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/migracion/traspaso", { params: { clave } });
        setDoc(data);
        setItems(data.items.map((i) => ({ ...i, cantidad: i.cantidad ?? 0 })));
        setMotivo(data.ajuste?.motivo || "");
      } catch (e) { toast.error(formatApiError(e)); onClose?.(); }
    })();
  }, [clave, onClose]);

  const subtotal = useMemo(() => items.reduce((a, i) => a + (i.excluida ? 0 : (Number(i.precio_unitario) || 0) * (Number(i.cantidad) || 0)), 0), [items]);

  function setLinea(idx, patch) { setItems((prev) => prev.map((i, k) => (k === idx ? { ...i, ...patch } : i))); }

  async function guardar() {
    setGuardando(true);
    try {
      const lineas = items.map((i) => ({ indice: i.indice, cantidad: Number(i.cantidad) || 0, excluida: !!i.excluida }));
      const { data } = await api.post("/migracion/traspaso/ajustar", { clave, lineas, motivo, resolver_conflicto: true });
      toast.success(`Traspaso ${data.folio} ajustado · nuevo valor ${money(data.subtotal)}${data.excluidas ? ` · ${data.excluidas} línea(s) excluida(s)` : ""}`);
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setGuardando(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-6" data-testid="editar-traspaso-modal">
      <div className="surface-card w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-start justify-between gap-3 p-4 border-b border-brand-line">
          <div>
            <h3 className="font-display font-bold text-brand-ink">Editar traspaso histórico {doc?.folio || ""}</h3>
            {doc && <p className="text-xs text-brand-muted mt-0.5">{doc.origen_nombre} → {doc.destino_nombre}. Ajusta las cantidades o excluye líneas capturadas por error en Defontana. El estado de resultados se recalcula al guardar.</p>}
          </div>
          <button data-testid="editar-traspaso-cerrar" onClick={onClose} className="text-brand-muted hover:text-brand-ink"><X className="w-5 h-5" /></button>
        </div>

        {!doc ? (
          <div className="p-10 flex items-center justify-center text-brand-muted"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando traspaso…</div>
        ) : (
          <>
            <div className="overflow-auto flex-1">
              <table className="w-full text-xs">
                <thead className="table-head sticky top-0"><tr>
                  <th className="text-left px-3 py-2">SKU</th><th className="text-left px-3 py-2">Insumo</th>
                  <th className="text-right px-3 py-2">Costo unit.</th><th className="text-right px-3 py-2">Cantidad</th>
                  <th className="text-right px-3 py-2">Importe</th><th className="text-center px-3 py-2">Excluir</th>
                </tr></thead>
                <tbody>
                  {items.map((i, idx) => (
                    <tr key={i.indice} className={`border-t border-brand-line/70 ${i.excluida ? "opacity-50" : ""}`} data-testid={`traspaso-linea-${idx}`}>
                      <td className="px-3 py-1.5 font-mono">{i.sku}</td>
                      <td className="px-3 py-1.5 max-w-[220px] truncate">{i.nombre}</td>
                      <td className="px-3 py-1.5 text-right whitespace-nowrap">{money(i.precio_unitario)}</td>
                      <td className="px-3 py-1.5 text-right">
                        <input data-testid={`traspaso-cant-${idx}`} type="number" min="0" step="any" disabled={i.excluida}
                          value={i.cantidad} onChange={(e) => setLinea(idx, { cantidad: e.target.value })}
                          className="field h-8 w-28 text-right disabled:opacity-40" />
                      </td>
                      <td className="px-3 py-1.5 text-right whitespace-nowrap font-semibold">{money(i.excluida ? 0 : (Number(i.precio_unitario) || 0) * (Number(i.cantidad) || 0))}</td>
                      <td className="px-3 py-1.5 text-center">
                        <input data-testid={`traspaso-excluir-${idx}`} type="checkbox" checked={!!i.excluida} onChange={(e) => setLinea(idx, { excluida: e.target.checked })} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-brand-line space-y-3">
              {doc.total_defontana != null && Math.abs((doc.total_defontana || 0) - subtotal) > 1 && (
                <div className="flex items-center gap-2 text-xs text-brand-amber"><AlertTriangle className="w-4 h-4 shrink-0" /> Valor en Defontana: {money(doc.total_defontana)} · nuevo valor RubiPOS: {money(subtotal)}</div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-brand-slate">Nuevo valor del traspaso</span>
                <span className="font-display font-bold text-lg text-brand-ink" data-testid="traspaso-subtotal">{money(subtotal)}</span>
              </div>
              <textarea data-testid="traspaso-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2}
                placeholder="Motivo del ajuste (opcional): p. ej. cantidades atípicas capturadas por error en Defontana" className="field w-full text-sm" />
              <div className="flex justify-end gap-2">
                <button data-testid="traspaso-cancelar" onClick={onClose} className="btn-soft h-9 px-4 text-sm">Cancelar</button>
                <button data-testid="traspaso-guardar" onClick={guardar} disabled={guardando} className="btn-amber h-9 px-4 text-sm">
                  {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar y marcar revisado
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
